import React, { useEffect, useRef, useState } from 'react';
import { useIdHotelOwner } from '../../../../../contexts/HotelOwnerContext';
import { useNotification } from '../../../../../contexts/NotificationsContext';
import useLanguage from '../../../../../customHooks/useLanguage';
import useTranslate from '../../../../../customHooks/useTranslate';
import Button from '../../../../../muiComponents/Button';
import IconButton from '../../../../../muiComponents/IconButton';
import LoadingPlaceholder from '../../../../../muiComponents/LoadingPlaceholder';
import { commonIcons } from '../../../../../utils/commonVariables/commonIcons';
import { FeedbackMessages } from '../../../../../utils/commonVariables/messages';
import { useServiceScraperPanel } from '../../../contexts/ServiceScraperPanelContext';
import { useZitoneoHotelPresentation } from '../../../contexts/ZitoneoContentContext';
import { getServiceScrapingHistory } from '../../../services/serviceScrapingStorage';
import { StoredServiceScraping } from '../../../types/serviceScrapingHistoryTypes';
import NoScrapingHistory from './NoScrapingHistory';

const ServiceScrapingHistory = () => {
    const translate = useTranslate();
    const language = useLanguage();
    const ownerId = useIdHotelOwner();
    const { setErrorMessage } = useNotification();
    const notifyRef = useRef(setErrorMessage);
    notifyRef.current = setErrorMessage;

    const { hotel, isLoading } = useZitoneoHotelPresentation();
    const { openPanelFromResponse } = useServiceScraperPanel();
    const [revision, setRevision] = useState(0);
    const hotelId = hotel.idHotel ? hotel.idHotel.toString() : '';
    const scopeKey = JSON.stringify([ownerId, hotelId]);
    const [state, setState] = useState<{
        scopeKey: string;
        records: StoredServiceScraping[];
        isLoading: boolean;
        hasError: boolean;
    }>({ scopeKey, records: [], isLoading: true, hasError: false });

    useEffect(() => {
        let cancelled = false;
        if (ownerId && hotelId) {
            setState({
                scopeKey,
                records: [],
                isLoading: true,
                hasError: false,
            });
            const loadHistory = async () => {
                try {
                    const records = await getServiceScrapingHistory({
                        ownerId,
                        hotelId,
                    });
                    if (!cancelled) {
                        setState({
                            scopeKey,
                            records,
                            isLoading: false,
                            hasError: false,
                        });
                    }
                } catch (error) {
                    if (!cancelled) {
                        console.error(
                            'Could not load scraping history:',
                            error
                        );
                        setState({
                            scopeKey,
                            records: [],
                            isLoading: false,
                            hasError: true,
                        });
                        notifyRef.current(FeedbackMessages.COMMON_ERROR);
                    }
                }
            };
            void loadHistory();
        }
        return () => {
            cancelled = true;
        };
    }, [ownerId, hotelId, scopeKey, revision]);

    const handleView = (record: StoredServiceScraping) => {
        try {
            openPanelFromResponse(record.response);
        } catch (error) {
            console.error('Could not open scraping history result:', error);
            setErrorMessage(FeedbackMessages.COMMON_ERROR);
        }
    };

    const isLoadingHistory =
        isLoading || state.scopeKey !== scopeKey || state.isLoading;
    const dateFormatter = new Intl.DateTimeFormat(language, {
        dateStyle: 'medium',
        timeStyle: 'short',
    });

    return (
        <div className='service-scraper__history' aria-busy={isLoadingHistory}>
            {isLoadingHistory ? (
                <LoadingPlaceholder
                    variant='rounded'
                    width='100%'
                    height={80}
                    aria-hidden='true'
                />
            ) : state.hasError ? (
                <div role='alert'>
                    <p>{translate('common_error_message')}</p>
                    <Button
                        type='button'
                        onClick={() => setRevision(previous => previous + 1)}
                    >
                        {translate('retry')}
                    </Button>
                </div>
            ) : state.records.length ? (
                <ul className='service-scraper__history-list'>
                    {state.records.map(record => {
                        const serviceCount = record.response.Results.reduce(
                            (total, category) =>
                                total +
                                category.ScrapingServiceResponse.KnownServices
                                    .length,
                            0
                        );
                        const date = dateFormatter.format(
                            new Date(record.analyzedAt)
                        );
                        return (
                            <li
                                key={record.id}
                                className='service-scraper__history-row'
                            >
                                <div>
                                    <div className='flex flex-align-baseline gap-20'>
                                        <time dateTime={record.analyzedAt}>
                                            {date}
                                        </time>
                                        <span>
                                            {serviceCount}{' '}
                                            {translate('services')}
                                        </span>
                                    </div>
                                    {!record.response.Result ? (
                                        <p className='service-scraper__history-status'>
                                            {translate('failed')}
                                        </p>
                                    ) : null}
                                </div>
                                <IconButton
                                    type='button'
                                    color='secondary'
                                    iconName={commonIcons.VIEW}
                                    disabled={!record.response.Result}
                                    aria-label={`${translate(
                                        'view_analysis'
                                    )}: ${date}`}
                                    onClick={() => handleView(record)}
                                />
                            </li>
                        );
                    })}
                </ul>
            ) : (
                <NoScrapingHistory />
            )}
        </div>
    );
};

export default ServiceScrapingHistory;
